import React, { useMemo } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { bindActionCreators } from 'redux';
import { withTranslation, WithTranslation } from 'react-i18next';
import DocumentTitle from '../../components/documentTitle/documentTitle';

import TemplateList from '../../components/templateList/templateList';
import ContentView from '../../components/contentView/contentView';
import ViewHeader from '../../components/viewHeader/viewHeader';
import BackButton from '../../components/backButton/backButton';
import { AppDispatch, ViewOwnProps } from '../../definitions/types';
import { AppState } from '../../redux/reducers';
import templateImages, { getFilteredTemplateImages, Template } from '../../modules/templateImages';
import { getIndustries } from '../../modules/industries';
import {
  navigate as navigateAction,
  changeViewToIndustry as changeViewToIndustryAction,
} from '../../redux/actions/router';
import {
  setSubtype as setSubtypeAction,
  increaseAmount as increaseAmountAction,
  resetAmount as resetAmountAction,
} from '../../redux/actions/template';

import './templateView.scss';
import { trackDWH } from '../../modules/tracking';

interface StateProps {
  amountTemplates: number;
  industry: string;
}

interface DispatchProps {
  setSubtype: typeof setSubtypeAction;
  increaseAmount: typeof increaseAmountAction;
  resetAmount: typeof resetAmountAction;
  navigate: typeof navigateAction;
  changeViewToIndustry: typeof changeViewToIndustryAction;
}

type TemplateViewProps = DispatchProps & StateProps & ViewOwnProps & WithTranslation;

export class TemplateView extends React.Component<TemplateViewProps, {}> {
  mountTime: number = Date.now();

  constructor(props: TemplateViewProps) {
    super(props);
    this.handleSelect = this.handleSelect.bind(this);
    this.showMore = this.showMore.bind(this);
    this.backToIndustries = this.backToIndustries.bind(this);
    props.resetAmount();
  }

  componentDidMount(): void {
    trackDWH('template-view', 'mount', {
      auto: true,
    });
    this.mountTime = Date.now();
  }

  componentWillUnmount(): void {
    trackDWH('template-view', 'unmount', {
      auto: true,
      mountTimeMs: Date.now() - this.mountTime,
    });
  }

  public render(): JSX.Element {
    const filteredTemplateImages: Template[] = getFilteredTemplateImages(
      templateImages,
      this.props.industry
    );

    return (
      <DocumentTitle title={this.props.t('signup-flow.document-title.template')}>
        <ContentView>
          <BackButton
            label={this.props.t('signup-flow.misc.backButton.label')}
            onClick={this.backToIndustries}
            className="template-view__button--desktop"
          />
          <ViewHeader
            title={this.props.t('signup-flow.template.view.name')}
            headlineText={this.getHeadline()}
            sublineText={this.props.t('signup-flow.template.description')}
          />
          <BackButton
            label={this.props.t('signup-flow.misc.backButton.label')}
            onClick={this.backToIndustries}
            className="template-view__button--mobile"
          />
          <TemplateList
            onSelect={this.handleSelect}
            onShowMore={this.showMore}
            templateImages={filteredTemplateImages}
            buttonText={this.props.t('signup-flow.template.buttonText')}
            moreText={this.props.t('signup-flow.template.moreText')}
            amountTemplates={this.props.amountTemplates}
          />
        </ContentView>
      </DocumentTitle>
    );
  }

  private handleSelect(subtype: string): void {
    this.props.setSubtype(subtype);
    this.props.navigate(this.props.route.path || '');
  }

  private showMore(maxAmount: number): void {
    this.props.increaseAmount(maxAmount);
  }

  private getHeadline(): string {
    const { industry } = this.props;
    const industries = getIndustries(this.props.t);
    if (!industry || industry === 'showAll' || industry === 'other') {
      return this.props.t('signup-flow.template.headline');
    }
    return this.props
      .t('signup-flow.template.specific-headline')
      .replace(/\{industry\}/, String(industries[industry]));
  }

  private backToIndustries(): void {
    trackDWH('back-button', 'click', { currentStep: 'template-view' });
    this.props.changeViewToIndustry();
  }
}

const mapStateToProps = (state: AppState, ownProps: ViewOwnProps): StateProps => {
  const { amountTemplates } = state.template;
  const { industry } = state.intent;

  return { amountTemplates, industry };
};

const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return bindActionCreators(
    {
      setSubtype: setSubtypeAction,
      increaseAmount: increaseAmountAction,
      resetAmount: resetAmountAction,
      navigate: navigateAction,
      changeViewToIndustry: changeViewToIndustryAction,
    },
    dispatch
  );
};

const TranslatedTemplateView = withTranslation()(TemplateView);

const ConnectedTemplateView = (ownProps: ViewOwnProps): JSX.Element => {
  const stateProps = useSelector(
    (state: AppState) => mapStateToProps(state, ownProps),
    shallowEqual
  );
  const dispatch = useDispatch<AppDispatch>();
  const dispatchProps = useMemo(() => mapDispatchToProps(dispatch), [dispatch]);

  return <TranslatedTemplateView {...ownProps} {...stateProps} {...dispatchProps} />;
};

export default React.memo(ConnectedTemplateView);
